



































































































































import Vue from "vue";
import moment from "moment";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "@vue/composition-api";
import GaIcon from "../../card-items/GaIcon.vue";
import { useGenAiPalette } from "../../data/design";

/**
 * Bounds are the period the list covers (the dashboard's time frame), not the
 * rows that happen to be loaded, so the ruler does not shrink as pages
 * arrive. `from_ts`/`to_ts` null means every day.
 */
export default Vue.extend({
  components: { GaIcon },
  props: {
    min_ts: { type: Number, required: true },
    max_ts: { type: Number, required: true },
    from_ts: { type: Number, default: null },
    to_ts: { type: Number, default: null },
  },
  setup(props, { emit }) {
    const T = useGenAiPalette();
    const root = ref<HTMLElement | null>(null);
    const open = ref(false);

    const start = computed(() => moment(props.min_ts as number).startOf("day"));
    const max_idx = computed(() =>
      Math.max(0, moment(props.max_ts as number).startOf("day").diff(start.value, "days"))
    );
    const idx_of = (ts: number | null, fallback: number) =>
      ts === null || ts === undefined
        ? fallback
        : Math.min(max_idx.value, Math.max(0, moment(ts).startOf("day").diff(start.value, "days")));

    const from_idx = ref(0);
    const to_idx = ref(0);
    const sync = () => {
      from_idx.value = idx_of(props.from_ts as number | null, 0);
      to_idx.value = idx_of(props.to_ts as number | null, max_idx.value);
    };
    sync();
    watch(() => [props.from_ts, props.to_ts, props.min_ts, props.max_ts], sync, { lazy: true });

    const day = (idx: number) => start.value.clone().add(idx, "days");
    const short = (m: moment.Moment) => m.format("MMM D");

    const clamp_from = () => {
      if (from_idx.value > to_idx.value) from_idx.value = to_idx.value;
    };
    const clamp_to = () => {
      if (to_idx.value < from_idx.value) to_idx.value = from_idx.value;
    };

    const left_pct = computed(() => (max_idx.value ? (100 * from_idx.value) / max_idx.value : 0));
    const right_pct = computed(() => (max_idx.value ? (100 * to_idx.value) / max_idx.value : 100));

    const is_full = (from: number, to: number) => from === 0 && to === max_idx.value;

    const trigger_label = computed(() => {
      if (props.from_ts === null || props.to_ts === null) return "Any date";
      const from = short(moment(props.from_ts as number));
      const to = short(moment(props.to_ts as number));
      return from === to ? from : `${from} – ${to}`;
    });

    const fields = computed(() => [
      { label: "From", value: short(day(from_idx.value)) },
      { label: "To", value: short(day(to_idx.value)) },
    ]);

    const toggle = () => {
      if (open.value) sync();
      open.value = !open.value;
    };

    const reset = () => {
      from_idx.value = 0;
      to_idx.value = max_idx.value;
    };

    const apply = () => {
      if (is_full(from_idx.value, to_idx.value)) {
        emit("change", null);
      } else {
        emit("change", {
          from_ts: day(from_idx.value).valueOf(),
          to_ts: day(to_idx.value).endOf("day").valueOf(),
        });
      }
      open.value = false;
    };

    // closing without Apply puts the handles back where the list is
    const on_outside = (e: MouseEvent) => {
      if (open.value && root.value && !root.value.contains(e.target as Node)) {
        open.value = false;
        sync();
      }
    };
    onMounted(() => document.addEventListener("mousedown", on_outside));
    onBeforeUnmount(() => document.removeEventListener("mousedown", on_outside));

    const field = computed(() => ({
      gap: "8px",
      height: "34px",
      padding: "0 10px",
      minWidth: "136px",
      background: T.value.ev.inputBg,
      border: "1px solid " + T.value.ev.inputBorder,
      borderRadius: "6px",
      "--ga-field-hover": T.value.ev.inputBorderHover,
    }));

    return {
      T,
      root,
      open,
      max_idx,
      from_idx,
      to_idx,
      day,
      short,
      clamp_from,
      clamp_to,
      left_pct,
      right_pct,
      trigger_label,
      fields,
      toggle,
      reset,
      apply,
      field,
    };
  },
});
